import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Calendar, Star, Trash2, Loader2, Mail, ChevronRight } from "lucide-react";
import api from "../../lib/api";
import { PotwTestActions, SeasonGapWarning } from "./PotwTestActions";

export default function WeeklyPlantsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const rw = await api.get("/admin/weekly-plants?weeks_ahead=12&weeks_back=2");
      setRows(rw.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Konnte Wochen-Planer nicht laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setPlantForWeek = async (weekKey, plantId) => {
    if (!plantId) return;
    setSavingKey(weekKey);
    try {
      await api.put(`/admin/weekly-plants/${weekKey}`, { plant_id: plantId });
      toast.success(`Woche ${weekKey} gesetzt`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSavingKey("");
    }
  };

  const clearWeek = async (weekKey) => {
    if (!window.confirm(`Planung für Woche ${weekKey} entfernen? Ohne Planung greift die automatische Rotation.`)) return;
    setSavingKey(weekKey);
    try {
      await api.delete(`/admin/weekly-plants/${weekKey}`);
      toast.success("Planung entfernt");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen");
    } finally {
      setSavingKey("");
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
  };

  return (
    <div className="space-y-5" data-testid="weekly-plants-panel">
      <div className="flex items-start gap-3">
        <Calendar className="w-6 h-6 mt-1" style={{ color: "var(--wp-olive)" }} />
        <div>
          <h2 className="font-display text-2xl">Pflanze der Woche – Planung</h2>
          <p className="text-sm opacity-70 max-w-2xl">
            Plane für die nächsten 12 Wochen. Jeden <b>Montag 00:00</b> wechselt die Pflanze automatisch auf deine Auswahl,
            und Montag <b>09:00</b> geht der Newsletter an alle Abonnenten raus. Ohne Planung greift eine automatische Rotation.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm opacity-70">
          <Loader2 className="w-4 h-4 animate-spin" /> Lädt …
        </div>
      ) : (
        <>
        <SeasonGapWarning rows={rows} />
        <NextFourPreview rows={rows} savingKey={savingKey} onSwap={setPlantForWeek} />
        <div className="space-y-2">
          {rows.map((r) => {
            const currentPlantId = r.scheduled_plant?.id || "";
            const fallbackName = r.fallback_plant?.name_de || "—";
            const isSaving = savingKey === r.week_key;
            return (
              <div
                key={r.week_key}
                className={`plant-card p-3 sm:p-4 grid grid-cols-1 sm:grid-cols-[130px_1fr_1fr_auto] gap-2 sm:gap-4 items-center ${
                  r.is_current ? "ring-2 ring-[var(--wp-primary)]" : ""
                }`}
                style={r.is_past ? { opacity: 0.65 } : {}}
                data-testid={`weekly-row-${r.week_key}`}
              >
                {/* Week label */}
                <div>
                  <div className="text-xs opacity-60">KW {r.iso_week} / {r.iso_year}</div>
                  <div className="text-sm font-medium">
                    {fmtDate(r.monday)} – {fmtDate(r.sunday)}
                  </div>
                  {r.is_current && (
                    <div className="inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[var(--wp-primary)] text-[var(--wp-cream)]">
                      diese Woche
                    </div>
                  )}
                </div>

                {/* Scheduled plant */}
                <div className="flex items-center gap-2">
                  {r.scheduled_plant?.image_url && (
                    <img
                      src={r.scheduled_plant.image_url}
                      alt=""
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <select
                      value={currentPlantId}
                      onChange={(e) => setPlantForWeek(r.week_key, e.target.value)}
                      disabled={isSaving || r.is_past}
                      className="w-full px-2 py-2 rounded-lg border text-sm bg-white"
                      style={{ borderColor: "var(--wp-border)" }}
                      data-testid={`weekly-select-${r.week_key}`}
                    >
                      <option value="">— nicht geplant (Rotation) —</option>
                      {(r.candidates || []).map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name_de}
                        </option>
                      ))}
                    </select>
                    {!currentPlantId && (
                      <div className="text-xs opacity-60 mt-1 flex items-center gap-1">
                        <ChevronRight className="w-3 h-3" /> Auto: <span className="font-medium">{fallbackName}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Newsletter status */}
                <div className="text-xs">
                  {r.newsletter_sent_at ? (
                    <div className="flex items-center gap-1 text-[var(--wp-primary)]">
                      <Mail className="w-3.5 h-3.5" />
                      Newsletter am{" "}
                      {new Date(r.newsletter_sent_at).toLocaleDateString("de-DE", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "2-digit",
                      })}{" "}
                      versendet
                    </div>
                  ) : (
                    <div className="opacity-50">Newsletter noch nicht versendet</div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 justify-end">
                  {isSaving && <Loader2 className="w-4 h-4 animate-spin opacity-60" />}
                  {currentPlantId && !r.is_past && (
                    <button
                      onClick={() => clearWeek(r.week_key)}
                      className="btn-secondary text-xs !py-1.5 !px-2"
                      data-testid={`weekly-clear-${r.week_key}`}
                      title="Planung entfernen"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {r.is_current && r.scheduled_plant && (
                    <Star className="w-4 h-4" style={{ color: "var(--wp-primary)" }} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
        </>
      )}
    </div>
  );
}

function NextFourPreview({ rows, savingKey, onSwap }) {
  const next = rows.filter((r) => !r.is_past && !r.newsletter_sent_at).slice(0, 4);
  const [testTo, setTestTo] = useState(() => localStorage.getItem("potw_test_to") || "");
  const changeTo = (v) => { setTestTo(v); localStorage.setItem("potw_test_to", v); };
  return (
    <div className="plant-card p-4" data-testid="potw-next-four">
      <div className="overline mb-1">Nächste 4 Newsletter</div>
      <p className="text-xs opacity-70 mb-3">Automatisch nur heimische Wildpflanzen mit echtem Foto, die gerade Saison haben. Zum Tauschen eine andere Pflanze wählen.</p>
      <label className="flex flex-wrap items-center gap-2 text-xs mb-3">
        Test-Mail an
        <input type="email" value={testTo} onChange={(e) => changeTo(e.target.value)} placeholder="leer = deine Admin-Adresse"
          className="px-3 py-1.5 rounded-full border bg-white text-xs w-72" style={{ borderColor: "var(--wp-border)" }} data-testid="potw-test-to-input" />
      </label>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {next.map((r) => {
          const p = r.scheduled_plant || r.fallback_plant;
          return (
            <div key={r.week_key} className="rounded-xl border p-3 space-y-2" style={{ borderColor: "var(--wp-border)" }} data-testid={`potw-next-${r.week_key}`}>
              <div className="text-xs opacity-60">KW {r.iso_week} · {r.scheduled_plant ? "geplant" : "automatisch"}</div>
              {p?.image_url && <img src={p.image_url} alt="" className="w-full h-24 object-cover rounded-lg" />}
              <div className="font-medium text-sm" data-testid={`potw-next-name-${r.week_key}`}>{p?.name_de || "Keine passende Pflanze – kein Versand"}</div>
              <select
                value=""
                onChange={(e) => onSwap(r.week_key, e.target.value)}
                disabled={savingKey === r.week_key}
                className="w-full px-2 py-1.5 rounded-lg border text-xs bg-white"
                style={{ borderColor: "var(--wp-border)" }}
                data-testid={`potw-swap-${r.week_key}`}
              >
                <option value="">Tauschen gegen …</option>
                {(r.candidates || []).filter((c) => c.id !== p?.id).map((c) => (
                  <option key={c.id} value={c.id}>{c.name_de}</option>
                ))}
              </select>
              <PotwTestActions weekKey={r.week_key} disabled={!p} to={testTo} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
